import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  Button,
  DatePicker,
  EmptyState,
  Flex,
  IconButton,
  Input,
  Text
} from 'onefaceui-react-components';
import { push } from 'connected-react-router';

import Loading from 'react/components/Loading';
import { formatDate, getResourceTo } from 'react/utils/utils';
import { actionCreators } from 'react/api/actions/modals';
import { useEventLog } from 'react/queries/EventLog'

import SearchIcon from 'static/images/monet/search_field.svg';
import DateIcon from 'static/images/fields/date_field.svg';

import {
  Container,
  Event,
  Filters,
  SearchInput,
  ToggleButton,
  Header
} from './styles';
import Shortcut from '../components/Shortcut';

function RouteEvents({ routeId }) {
  const { data: dataEvents = [], isFetching } = useEventLog(routeId);
  const [filters, setFilters] = useState({
    search: '',
    exibitionMode: {
      normal: false,
      urgent: false,
      blocker: false
    },
    date: {
      start: '',
      end: '',
      isOpen: false
    }
  });
  const eventListRef = useRef(null);
  const eventLogTabExists = useSelector(state => state.tabs.hasIn(['list', 'EventLog']));
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const dispatch = useDispatch();

  const footer = useSelector((state) => state.footer);
  const miniDashboardIsOpen = footer.get('miniDashboardIsOpen');

  const shortcutMenus = [
    {
      iconClass: 'MonetUIMenuRouteIcon viewDetails',
      type: 'view-event-details',
      name: getResourceTo('routemonitor.routePanel.events.viewEventDetails'),
      onClick: ({ id }) => {
        dispatch(actionCreators.openModal({
          modalKey: 'eventDetails',
          title: getResourceTo('eventDetails.modal.title', 'Event Details'),
          data: {
            eventLogId: String(id)
          }
        }));
      }
    },
    {
      iconClass: 'MonetUIMenuRouteIcon viewEventLog',
      type: 'view-event-log',
      name: getResourceTo('routemonitor.routePanel.events.viewOnEventLog'),
      onClick: (eventLog) => {
        const { event, timeValuedTrue, timeValuedFalse } = eventLog;

        Monet.Session.EventLog = {
          filters: {
            id: eventLog.id,
            event,
            timeValuedTrue,
            timeValuedFalse,
            clearFilters: true
          }
        };

        dispatch(push('/EventLog'));

        if (eventLogTabExists) {
          App.Objects.EventLog.list()
        }
      }
    },
  ];

  const handleChangeSearch = ({ target }) => {
    setFilters((prev) => ({ ...prev, search: target.value }));
  };

  const handleClickToggleBtn = (btnType) => {
    setFilters(({ exibitionMode, ...rest }) => ({
      ...rest,
      exibitionMode: {
        ...exibitionMode,
        [btnType]: !exibitionMode[btnType]
      }
    }));
  };

  const handleClickBtnDate = () => {
    setFilters(({ date, ...rest }) => ({
      ...rest,
      date: { start: '', end: '', isOpen: !date.isOpen }
    }));
  };

  const handleChangeDate = (value, field) => {
    setFilters(({ date, ...rest }) => ({
      ...rest,
      date: {
        ...date,
        [field]: value || ''
      }
    }));
  };

  const clearIntervalFilters = () => {
    setFilters((prev) => ({
      ...prev,
      date: { isOpen: true, start: '', end: '' }
    }));
    startDateRef?.current?.clear();
    endDateRef?.current?.clear();
  };

  const getDateFormmated = () => {
    const format = Monet.I18n.locale.dateFormat.toLowerCase();
    return format.replace('mm', 'MM');
  };

  const getFilteredventsByText = useCallback((events, currentFilters) => {
    const { search } = currentFilters;

    if (!search) return events;

    const searchLowerCase = search.toLowerCase();

    return events.filter(({ event }) => {
      const eventName = event?.description?.toLowerCase() || "";

      return eventName.includes(searchLowerCase);
    });
  }, []);

  const getEventsByDateRange = useCallback((events, currentFilters) => {
    const { date: { start, end } } = currentFilters;

    if (!start || !end) return events;

    const startDate = moment(start).startOf('day');
    const endDate = moment(end).endOf('day');

    return events.filter((event) => {
      const eventDate = moment(event.timeValuedTrue);
      return eventDate.isBetween(startDate, endDate, null, []);
    });
  }, []);

  const getEventsByExibitionMode = useCallback((events, currentFilters) => {
    const exibitionModesActives = Object.entries(currentFilters.exibitionMode).filter(
      ([, isChecked]) => isChecked
    );

    const exibitionModes = exibitionModesActives.map(([type]) => type.toUpperCase());

    if (!exibitionModes.length) return events;

    return events.filter((eventLog) => {
      if (exibitionModes.includes(eventLog.event.exibitionMode)) {
        return eventLog;
      }
      return false;
    });
  }, []);

  const filteredEvents = useMemo(() => {
    const eventsByModes = getEventsByExibitionMode(dataEvents, filters);
    const eventsByDateRange = getEventsByDateRange(eventsByModes, filters);
    const eventsBySearch = getFilteredventsByText(eventsByDateRange, filters);

    return eventsBySearch;
  }, [dataEvents, filters]);

  useEffect(() => {
    const modalHeight = document.querySelector('.route-panel')?.clientHeight || 0;
    const modalHeaderHeight = document.querySelector('.route-panel .header')?.clientHeight || 0;
    const tabHeight = document.querySelector('.react-tabs__tab-list')?.clientHeight || 0;
    const filtersHeight = document.querySelector('.panel-route-header')?.clientHeight || 0;

    const totalHeader = modalHeaderHeight + tabHeight + filtersHeight;

    const listHeight = modalHeight - totalHeader;

    eventListRef?.current?.setAttribute('style', `height: ${listHeight}px`);
  }, [miniDashboardIsOpen, isFetching, filters.date]);

  return (
    <Container flexDirection="column">
      <Header className="panel-route-header" flexDirection="column" gap={6} dataTestId="panel-route-events">
        <Flex flexDirection="column">
          <SearchInput>
            <Input
              placeholder={getResourceTo('routemonitor.tab.events.search')}
              type="search"
              onChange={handleChangeSearch}
              dataTestId="search-input"
            />
            <img src={SearchIcon} alt="Search" />
          </SearchInput>
          <Filters alignItems="center" gap={12}>
            <Text size="xs">
              {getResourceTo('monet.routemonitor.label.showonly')}
            </Text>
            <Flex alignItems="center" gap={4}>
              <ToggleButton
                type="button"
                className={`normal ${
                  filters.exibitionMode.normal ? 'active' : ''
                }`}
                onClick={() => handleClickToggleBtn('normal')}
                data-testid="normal-events-btn"
              >
                {getResourceTo('event.exibitionmode.normal')}
              </ToggleButton>
              <ToggleButton
                className={`urgent ${
                  filters.exibitionMode.urgent ? 'active' : ''
                }`}
                type="button"
                onClick={() => handleClickToggleBtn('urgent')}
                data-testid="urgent-events-btn"
              >
                {getResourceTo('event.exibitionmode.urgent')}
              </ToggleButton>
              <ToggleButton
                className={`blocker ${
                  filters.exibitionMode.blocker ? 'active' : ''
                }`}
                type="button"
                onClick={() => handleClickToggleBtn('blocker')}
                data-testid="blocker-events-btn"
              >
                {getResourceTo('event.exibitionmode.blocker')}
              </ToggleButton>
              <IconButton data-testid="date-icon-btn" onClick={handleClickBtnDate}>
                <img src={DateIcon} alt="Date Icon" />
              </IconButton>
            </Flex>
          </Filters>
          {filters.date.isOpen && (
            <Flex flexDirection="column" className="date-filter" gap={8}>
              <Flex gap={8}>
                <DatePicker
                  locale={Monet.I18n.locale.language}
                  dateFormat={getDateFormmated()}
                  onChangeValue={(value) => handleChangeDate(value, 'start')}
                  dataTestId="start-date"
                  ref={startDateRef}
                />
                <DatePicker
                  locale={Monet.I18n.locale.language}
                  dateFormat={getDateFormmated()}
                  onChangeValue={(value) => handleChangeDate(value, 'end')}
                  dataTestId="end-date"
                  ref={endDateRef}
                />
              </Flex>
              <Button data-testid="clear-filters" variant="secondary" onClick={clearIntervalFilters}>
                {getResourceTo('routemonitor.btn.clearfilters')}
              </Button>
            </Flex>
          )}
        </Flex>
      </Header>
      <Flex
        flexDirection="column"
        className="events-list"
        ref={eventListRef}
        shouldShow={filteredEvents.length && !isFetching}
      >
        {filteredEvents.map((eventLog) => (
          <Event data-testid={`event-item-${eventLog.id}`} key={eventLog.id}>
            <Flex
              className={`status ${eventLog.event.exibitionMode.toLowerCase()}`}
              justifyContent="center"
              alignItems="center"
            />
            <Flex flexDirection="column" className="event-content" gap={4}>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex className="event-title" flexDirection="column">
                  <Text
                    weight="bold"
                    size="sm"
                    data-testid={`event-item-${eventLog.id}-description`}
                  >
                    {eventLog.event.description}
                  </Text>
                  <Text type="neutral" size="xs" data-testid={`event-item-${eventLog.id}-date`}>
                    {formatDate(eventLog.timeValuedTrue)}
                  </Text>
                </Flex>
                <Shortcut data={eventLog} menus={shortcutMenus} />
              </Flex>
              <Text
                className="event-description"
                size="xs"
                data-testid={`event-item-${eventLog.id}-message`}
                title={eventLog.messageBody}
              >
                {eventLog.messageBody}
              </Text>
            </Flex>
          </Event>
        ))}
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="center"
        style={{ height: '100%' }}
        shouldShow={isFetching}
      >
        <Loading />
      </Flex>
      <Flex
        flexDirection="column"
        justifyContent="center"
        style={{ flex: 1 }}
        shouldShow={!filteredEvents.length && !isFetching}
      >
        <EmptyState />
      </Flex>

    </Container>
  );
}

RouteEvents.propTypes = {
  routeId: PropTypes.number.isRequired
};


export default RouteEvents;
